import { useEffect, useRef, useState } from 'react';
import { FileText, Upload } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/context/ToastContext';
import GlobalLoader from '../components/context/GlobalLoader';
import { API_BASE } from '../lib/apiConfig';
import { trackCustomEvent, trackInitiateCheckout } from '../lib/fbpixel';
import { trackRedditAddToCart } from '../lib/redditPixel';
import {
  phInitiateCheckout,
  phEobSubmitted,
  phEobReportViewed,
  phFailure,
} from '../lib/posthog';
import { getMetaTrackingContext } from '../lib/checkoutContext';
import '@styles/pages/eob.css';

type EobStatus = 'submitted' | 'in_review' | 'report_ready';

interface EobReview {
  id: string;
  status: EobStatus;
  file_name: string | null;
  file_path: string | null;
  insurance_company: string | null;
  notes: string | null;
  report_path: string | null;
  report_notes: string | null;
  findings_count: number | null;
  created_at: string;
}

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

/* ── Helpers ─────────────────────────────────────────────── */
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/* ══════════════════════════════════════════════════════════
   EOB REVIEW PAGE
══════════════════════════════════════════════════════════ */
export default function EobReview() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [review, setReview] = useState<EobReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [upgradingToOnOff, setUpgradingToOnOff] = useState(false);
  const [upgradingToAnnual, setUpgradingToAnnual] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [file, setFile] = useState<File | null>(null);
  const [insuranceCompany, setInsuranceCompany] = useState('');
  const [notes, setNotes] = useState('');

  /* ── Load existing review ── */
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from('eob_reviews')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setReview(data ?? null);
      setLoading(false);
    };
    load();
  }, [user]);

  /* ── Real-time status updates ── */
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`eob-review-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'eob_reviews',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        setReview(payload.new as EobReview);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  /* ── File handling ── */
  function handleFile(f: File) {
    setError('');
    if (!ACCEPTED_TYPES.includes(f.type)) {
      setError('Please upload a PDF, JPG, or PNG file.');
      return;
    }
    if (f.size > MAX_SIZE) {
      setError('File must be under 10MB.');
      return;
    }
    setFile(f);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }

  /* ── Submit EOB ── */
  async function handleSubmit() {
    if (!file || !user) return;
    if (!insuranceCompany.trim()) { setError('Please enter your insurance company name.'); return; }
    setSubmitting(true);
    setError('');

    try {
      const filePath = `eob/${user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('medical_bills')
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      const { data, error: insertError } = await supabase
        .from('eob_reviews')
        .insert({
          user_id: user.id,
          file_path: filePath,
          file_name: file.name,
          insurance_company: insuranceCompany.trim(),
          notes: notes.trim() || null,
          status: 'submitted',
        })
        .select()
        .single();
      if (insertError) throw insertError;

      setReview(data);
      showToast('EOB submitted, we\'ll review it within 48 hours', 'success');
      // Meta: custom event so we can build lookalikes of users who actually
      // engage past sign-up. EOB upload is a strong intent signal  
      // these users typically upgrade to Per Bill after the review.
      void trackCustomEvent({
        name: 'EobUploaded',
        eventID: data.id,
        customData: { insurance_company: insuranceCompany.trim() },
        email: user.email || undefined,
      });
      /* ⚠️ 2026-08-08: there was no PostHog eob_* event of any kind. Meta knew
         more about this product line than our own product analytics did. */
      phEobSubmitted({ fileType: file.type || undefined, fileCount: 1 });
    } catch (err) {
      phFailure({
        step: 'eob_submit',
        message: (err as Error).message,
      });
      setError((err as Error).message || 'Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Upgrade to full negotiation ──
   * Calls the authenticated /stripe/create-eob-upgrade-checkout endpoint
   * which credits the $69 EOB toward the $129 Per Bill (customer pays $60).
   */
  async function handleUpgrade() {
    if (!user || upgradingToOnOff) return;
    setUpgradingToOnOff(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${API_BASE}/stripe/create-eob-upgrade-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // Forward Meta CAPI tracking context so the webhook can fire
        // an authoritative server-side Purchase with the same browser
        // identifiers Meta saw on the original ad click.
        body: JSON.stringify(getMetaTrackingContext()),
      });
      const data = await res.json();
      if (!res.ok || !data?.url) throw new Error(data?.error || 'Checkout failed');

      // Funnel tracking. Mirrors what /plan does on the same upgrade click so
      // the EOB→Per Bill conversion shows up identically regardless of which
      // entry point the user used. eventID is a fresh UUID per click — each
      // click is treated as a distinct InitiateCheckout for funnel analysis.
      phInitiateCheckout('upgrade', { flow: 'eob_to_one_off', value: 60 });
      const eobUpgradeId = crypto.randomUUID();
      void trackInitiateCheckout({
        eventID: eobUpgradeId,
        value: 60, // $129 Per Bill − $69 EOB credit
        contentName: 'eob_to_one_off',
        email: user.email || undefined,
      });
      trackRedditAddToCart({
        eventID: eobUpgradeId,
        value: 60,
        contentName: 'eob_to_one_off',
        email: user.email || undefined,
        externalId: user.id,
      });

      // Carry the already-uploaded EOB file path into the new bill submission
      if (review?.file_path) {
        localStorage.setItem('eob_upgrade_path', review.file_path);
      }

      window.location.href = data.url;
    } catch (err) {
      console.error('Upgrade error:', err);
      phFailure({
        step: 'checkout_create_eob_to_one_off',
        message: (err as Error).message,
      });
      showToast('Something went wrong. Please try again.', 'error');
      setUpgradingToOnOff(false);
    }
  }

  /* ── Upgrade to Annual ──
   * Calls /stripe/create-upgrade-checkout which already supports EOB→Annual:
   * $499 list, $69 EOB credit applied as a one-time coupon → $430 net.
   * The webhook transitions the subscription row from plan='eob' to
   * plan='annual' on success.
   */
  async function handleAnnualUpgrade() {
    if (!user || upgradingToAnnual) return;
    setUpgradingToAnnual(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${API_BASE}/stripe/create-upgrade-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (!res.ok || !data?.url) throw new Error(data?.error || 'Checkout failed');

      // Funnel tracking — mirrors /plan's handleUpgrade so EOB→Annual reads
      // identically regardless of entry point. Standard InitiateCheckout is
      // what Meta's algorithm optimizes on; the custom event is for
      // lookalikes (EOB→Annual is the strongest LTV signal in the funnel).
      phInitiateCheckout('upgrade', { flow: 'eob_to_annual', value: 430 });
      const annualUpgradeId = crypto.randomUUID();
      void trackInitiateCheckout({
        eventID: annualUpgradeId,
        value: 430, // $499 Annual − $69 EOB credit
        contentName: 'eob_to_annual',
        email: user.email || undefined,
      });
      trackRedditAddToCart({
        eventID: annualUpgradeId,
        value: 430,
        contentName: 'eob_to_annual',
        email: user.email || undefined,
        externalId: user.id,
      });
      void trackCustomEvent({
        name: 'EobAnnualUpgradeStarted',
        eventID: annualUpgradeId,
        customData: { value: 430 },
        email: user.email || undefined,
      });

      window.location.href = data.url;
    } catch (err) {
      console.error('Annual upgrade error:', err);
      phFailure({
        step: 'checkout_create_eob_to_annual',
        message: (err as Error).message,
      });
      showToast('Something went wrong. Please try again.', 'error');
      setUpgradingToAnnual(false);
    }
  }

  /* ── Download report ── */
  async function handleDownload() {
    if (!review?.report_path) return;
    const { data } = await supabase.storage
      .from('medical_bills')
      .createSignedUrl(review.report_path, 60 * 60);
    if (data?.signedUrl) {
      // Meta: custom event marking the moment the user opened their
      // completed EOB report, peak intent for upgrading to Per Bill
      // negotiation. We dedup by review.id so multiple downloads of the
      // same review only fire once.
      void trackCustomEvent({
        name: 'EobReportViewed',
        eventID: review.id,
        email: user?.email || undefined,
      });
      phEobReportViewed({ reportId: review.id });
      window.open(data.signedUrl, '_blank');
    }
  }

  if (loading) return <GlobalLoader message="Loading your EOB review…" />;

  /* ══════════════════════
     STATE 1, UPLOAD
  ══════════════════════ */
  if (!review) {
    return (
      <div className="eob-container">
        <span className="eob-eyebrow">EOB Review</span>
        <h1 className="eob-heading">Send us <span className="italic">your EOB.</span></h1>
        <p className="eob-subheading">
          Upload your Explanation of Benefits and Agent Loop will review it within 48 hours,
          flagging errors, overcharges, and exactly what to dispute.
        </p>

        {error && <div className="eob-error">{error}</div>}

        {/* Upload zone */}
        <div
          className={`eob-upload-box ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
          />
          <div className="eob-upload-icon">
            <Upload size={18} color="#E97A50" />
          </div>
          {file ? (
            <>
              <div className="eob-upload-hint" style={{ color: '#0F6E56' }}>✓ File selected</div>
              <div className="eob-file-name">{file.name}</div>
              <div className="eob-upload-hint" style={{ marginTop: 4 }}>Click to change</div>
            </>
          ) : (
            <>
              <div className="eob-upload-hint"><strong>Click to upload</strong> or drag and drop</div>
              <div className="eob-upload-hint" style={{ marginTop: 4 }}>PDF, JPG or PNG · up to 10MB</div>
            </>
          )}
        </div>

        <div className="eob-field">
          <label className="eob-label">Insurance company</label>
          <input
            className="eob-input"
            type="text"
            placeholder="e.g. Blue Cross Blue Shield"
            value={insuranceCompany}
            onChange={(e) => setInsuranceCompany(e.target.value)}
          />
        </div>

        <div className="eob-field">
          <label className="eob-label">
            Anything specific that concerned you? <span className="optional">(optional)</span>
          </label>
          <textarea
            className="eob-textarea"
            placeholder="e.g. I was charged for a procedure I don't recognise…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <button
          className="eob-submit-btn"
          onClick={handleSubmit}
          disabled={submitting || !file}
        >
          {submitting ? 'Uploading…' : 'Submit for review'}
        </button>
      </div>
    );
  }

  /* ══════════════════════
     STATE 2, IN REVIEW
  ══════════════════════ */
  if (review.status === 'submitted' || review.status === 'in_review') {
    return (
      <div className="eob-container">
        <span className="eob-eyebrow">Under review</span>
        <h1 className="eob-heading">Your EOB is <span className="italic">being reviewed.</span></h1>
        <p className="eob-subheading">
          Agent Loop is investigating your Explanation of Benefits. You'll receive an email
          when your report is ready, usually within 48 hours.
        </p>

        {/* Submitted file */}
        <div className="eob-file-row">
          <div className="eob-file-icon">
            <FileText size={16} color="#E97A50" />
          </div>
          <div>
            <div className="eob-file-row-name">{review.file_name || 'EOB document'}</div>
            <div className="eob-file-row-meta">
              Submitted {formatDate(review.created_at)}
              {review.insurance_company ? ` · ${review.insurance_company}` : ''}
            </div>
          </div>
        </div>

        {/* Status timeline */}
        <div className="eob-status-card">
          <div className="eob-timeline">
            <div className="eob-timeline-step">
              <div className="eob-timeline-connector">
                <div className="eob-timeline-dot dot-done" />
                <div className="eob-timeline-line" />
              </div>
              <div className="eob-timeline-label">
                EOB received
                <div className="eob-timeline-sub">{formatDate(review.created_at)}</div>
              </div>
            </div>
            <div className="eob-timeline-step">
              <div className="eob-timeline-connector">
                <div className="eob-timeline-dot dot-active" />
                <div className="eob-timeline-line" />
              </div>
              <div className="eob-timeline-label active">
                Under review{' '}
                <span className="eob-badge badge-purple" style={{ marginLeft: 6 }}>In progress</span>
                <div className="eob-timeline-sub">Agent Loop is investigating</div>
              </div>
            </div>
            <div className="eob-timeline-step">
              <div className="eob-timeline-connector">
                <div className="eob-timeline-dot dot-pending" />
              </div>
              <div className="eob-timeline-label pending">
                Report delivered
                <div className="eob-timeline-sub">Within 48 hours</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quiet inline upsell — single sentence + one CTA. Anchors the
            upgrade path before the report lands, without competing with
            the timeline above. */}
        <div className="eob-wait-cta">
          <p className="eob-wait-cta-text">
            Got a bill that can&apos;t wait?{' '}
            <button
              className="eob-wait-cta-link"
              onClick={handleUpgrade}
              disabled={upgradingToOnOff || upgradingToAnnual}
            >
              {upgradingToOnOff ? 'Redirecting…' : 'Start full negotiation →'}
            </button>
          </p>
          <p className="eob-wait-cta-fine">
            Your $69 review credits toward Pay Per Bill ($60) or Annual ($430/yr).
          </p>
        </div>
      </div>
    );
  }

  /* ══════════════════════
     STATE 3, REPORT READY
  ══════════════════════ */
  return (
    <div className="eob-container">
      <span className="eob-eyebrow">Verdict</span>
      <h1 className="eob-heading">Your report <span className="italic">is ready.</span></h1>
      <p className="eob-subheading">
        Agent Loop has reviewed your EOB
        {review.insurance_company ? ` from ${review.insurance_company}` : ''}.
        {review.findings_count && review.findings_count > 0
          ? ` We found ${review.findings_count} issue${review.findings_count !== 1 ? 's' : ''} worth addressing.`
          : ''}
      </p>

      <div className="eob-report-card">
        <div className="eob-report-header">
          <div>
            <div className="eob-report-title">
              EOB Review{review.insurance_company ? `, ${review.insurance_company}` : ''}
            </div>
            {review.findings_count !== null && review.findings_count !== undefined && (
              <div style={{ marginTop: 6 }}>
                <span className={`eob-badge ${review.findings_count > 0 ? 'badge-amber' : 'badge-green'}`}>
                  {review.findings_count > 0
                    ? `${review.findings_count} issue${review.findings_count !== 1 ? 's' : ''} found`
                    : 'No issues found'}
                </span>
              </div>
            )}
          </div>
          <span className="eob-badge badge-green">Report ready</span>
        </div>

        {review.report_notes && (
          <p style={{ fontSize: 14, color: '#0E1530', lineHeight: 1.6, marginBottom: 16 }}>
            {review.report_notes}
          </p>
        )}

        {review.report_path && (
          <button className="eob-download-btn" onClick={handleDownload}>
            Download full report (PDF)
          </button>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────────────
          Upsell — quiet, two-card comparison + one social-proof line.
          Headline + one-line subtitle is all the intro this needs; the
          cards do the rest of the lifting. Different framing for the
          "findings" vs "no findings" branches, same layout in both so
          the page never re-shuffles.
      ────────────────────────────────────────────────────────────────── */}
      {(() => {
        const count = review.findings_count ?? 0;
        const hasFindings = count > 0;
        const pluralIssue = count === 1 ? 'issue' : 'issues';
        const pluralPronoun = count === 1 ? 'it' : 'them';

        return (
          <section className="eob-upsell-section">
            <header className="eob-upsell-header">
              <h2 className="eob-upsell-h">
                {hasFindings
                  ? `${count} ${pluralIssue} found. Let's recover ${pluralPronoun}.`
                  : 'Stay covered for what comes next.'}
              </h2>
              <p className="eob-upsell-sub">
                {hasFindings
                  ? 'Agent Loop will negotiate directly with your provider. Your $69 review credits toward either plan.'
                  : 'Your $69 review still credits toward either plan. Most people see another bill within 90 days.'}
              </p>
            </header>

            <div className="eob-upsell-grid">
              {/* ── Pay Per Bill ── */}
              <article className="eob-upsell-card">
                <div className="eob-upsell-card-label">Pay Per Bill</div>
                <div className="eob-upsell-card-price-row">
                  <span className="eob-upsell-card-amount">$60</span>
                  <span className="eob-upsell-card-was">$129</span>
                </div>
                <p className="eob-upsell-card-meta">$69 EOB credit applied</p>
                <p className="eob-upsell-card-desc">
                  Full negotiation on one medical bill. Money-back if no savings.
                </p>
                <button
                  className="eob-upsell-card-btn outline"
                  onClick={handleUpgrade}
                  disabled={upgradingToOnOff || upgradingToAnnual}
                >
                  {upgradingToOnOff ? 'Redirecting…' : 'Start bill negotiation'}
                </button>
                <div className="eob-upsell-card-reassure">
                  <span className="eob-upsell-card-reassure-icon" aria-hidden>✓</span>
                  No commitment · Money-back if no savings
                </div>
              </article>

              {/* ── Annual (recommended) — integrated "Most popular" ribbon ── */}
              <article className="eob-upsell-card recommended">
                <div className="eob-upsell-card-ribbon">Most popular</div>
                <div className="eob-upsell-card-label">Annual</div>
                <div className="eob-upsell-card-price-row">
                  <span className="eob-upsell-card-amount">$430</span>
                  <span className="eob-upsell-card-was">$499</span>
                </div>
                <p className="eob-upsell-card-meta">$69 EOB credit · save $275/year</p>
                <p className="eob-upsell-card-desc">
                  Six bill negotiations a year, unlimited EOB reviews, priority queue.
                  Roughly $72 per bill.
                </p>
                <button
                  className="eob-upsell-card-btn"
                  onClick={handleAnnualUpgrade}
                  disabled={upgradingToOnOff || upgradingToAnnual}
                >
                  {upgradingToAnnual ? 'Redirecting…' : 'Upgrade to Annual'}
                </button>
                <div className="eob-upsell-card-reassure">
                  <span className="eob-upsell-card-reassure-icon" aria-hidden>✓</span>
                  Cancel anytime · Same money-back guarantee
                </div>
              </article>
            </div>
          </section>
        );
      })()}
    </div>
  );
}
